// (2026-06-10) Maestro de Ordenes de Compra — PAGINA FORMULARIO.
//
// REDISEÑADO con EL MISMO DISEÑO que components/Proformas/FormProformas.js
// (el formulario canonico del sistema): card blueGray con titulo, h6 de
// seccion uppercase, <hr> separadores, cabecera en columnas w-X/12 con los
// inputs Tailwind canonicos, editor de items con boton MUI "Agregar línea" +
// <DataGrid> de @mui/x-data-grid (apiRef, processRowUpdate, locale esES,
// autoHeight, rowHeight 30, headers en negrita), bloque de totales (Base
// imponible / I.V.A. / Total con formatMoney) y footer FORM_FOOTER_CONTAINER
// con FORM_BTN_CANCEL/FORM_BTN_SUBMIT.
//
// CONSERVA toda la logica de negocio del modulo de Compras: ruta
// /compras/ordenes/create|edit?id=<hash> (decryptText + useLocation),
// catalogos getProveedores({activo:1})/getIvaCategorias, validate() con
// newErrors+errorToast, y createOrdenCompra/updateOrdenCompra con el payload
// EXACTO { ...form, items: [{descripcion, cantidad, precio_unitario,
// aplica_iva, iva_categoria_id}] } (filtrando items validos como siempre).
// Las filas del DataGrid llevan un `id` incremental interno que NO viaja en
// el payload.

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { Button } from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import AdminLayout from "../../layouts/AdminLayout";
import { decryptText } from "../../controllers/api";
import {
  getOrdenCompra, createOrdenCompra, updateOrdenCompra,
  getProveedores, getIvaCategorias,
} from "../../controllers/api_compras";
import { MONEDAS, monedaCodigo, handleApiError, hoyISO, upper } from "./comprasShared";
import { formatDecimal, formatMoney, monedaSimbolo } from "../../utils/formatters";
import {
  FORM_FOOTER_CONTAINER, FORM_BTN_CANCEL, FORM_BTN_SUBMIT, preventEnterSubmit,
} from "../../utils/formHelpers";

// Clase canonica de inputs/selects de cabecera (identica a FormProformas).
const INPUT_CLASS =
  "border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150";

// Fila vacia del DataGrid (lleva `id` interno para la grilla; el payload NO lo incluye).
const lineaVacia = (id) => ({ id, descripcion: "", cantidad: 1, precio_unitario: 0, aplica_iva: true, iva_categoria_id: "" });
const EMPTY = { proveedor_id: "", fecha_emision: hoyISO(), moneda_id: 3, condicion_pago: "", observaciones: "" };

export default function OrdenCompraForm() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // (igual que FormProformas) — id a editar via query param ?id= encriptado.
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const ocIdHash = queryParams.get("id");
  const isEdit = ocIdHash != null;

  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([lineaVacia(1)]);
  const [provs, setProvs] = useState([]);
  const [ivas, setIvas] = useState([]);
  const [estatus, setEstatus] = useState("borrador"); // solo informativo en edicion
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [errors, setErrors] = useState({}); // Estado para errores de validacion (estilo FormProformas)

  // Autofocus del DataGrid (mismo patron que la proforma).
  const apiRef = useGridApiRef();

  // Catalogos auxiliares (mismas llamadas que la pagina actual).
  useEffect(() => {
    getProveedores({ activo: 1 }).then(setProvs).catch(() => {});
    getIvaCategorias().then((d) => setIvas(Array.isArray(d) ? d : (d?.data || []))).catch(() => {});
  }, []);

  // Carga de la OC a editar (decryptText del id).
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const data = await getOrdenCompra(decryptText(ocIdHash));
        setForm({
          proveedor_id: data.proveedor_id ?? "",
          fecha_emision: (data.fecha_emision || "").slice(0, 10) || hoyISO(),
          moneda_id: data.moneda_id ?? 3,
          condicion_pago: data.condicion_pago || "",
          observaciones: data.observaciones || "",
        });
        const its = (data.items || []).map((it, idx) => ({
          id: idx + 1, // id incremental SOLO para la DataGrid
          descripcion: it.descripcion || "",
          cantidad: it.cantidad ?? 1,
          precio_unitario: it.precio_unitario ?? "",
          aplica_iva: !!it.iva_categoria_id,
          iva_categoria_id: it.iva_categoria_id || "",
        }));
        setItems(its.length ? its : [lineaVacia(1)]);
        setEstatus(data.estatus || "borrador");
      } catch (e) {
        handleApiError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [ocIdHash, isEdit]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Alicuota de un item (busca la categoria IVA seleccionada).
  const alicuotaDe = (it) =>
    it.aplica_iva ? (Number((ivas.find((x) => x.id === Number(it.iva_categoria_id)) || {}).tasa_porcentaje) || 0) : 0;

  // Totales (estilo resumen de la proforma).
  const { taxBase, taxApplied, totalAmount } = useMemo(() => {
    let base = 0, iva = 0;
    items.forEach((it) => {
      const sub = Number(it.cantidad || 0) * Number(it.precio_unitario || 0);
      const alic = alicuotaDe(it);
      base += sub;
      iva += sub * (alic / 100);
    });
    return { taxBase: base, taxApplied: iva, totalAmount: base + iva };
  }, [items, ivas]);

  // Objeto moneda para formatMoney (codigo + simbolo + posicion, como en Ventas).
  const monedaObj = useMemo(() => {
    const codigo = monedaCodigo(form.moneda_id) || "VES";
    return { codigo, simbolo: monedaSimbolo(codigo), posicion: codigo === "VES" ? "despues" : "antes" };
  }, [form.moneda_id]);

  const redirectToList = () => navigate("/compras/ordenes");

  // Agregar nueva línea con foco (mismo patron handleAddRow de la proforma).
  const handleAddRow = () => {
    const newId = items.length > 0 ? Math.max(...items.map((r) => r.id)) + 1 : 1;
    const newRow = lineaVacia(newId);

    setItems((prev) => {
      const updated = [...prev, newRow];

      setTimeout(() => {
        try {
          apiRef.current.setCellFocus(newId, "descripcion");
          apiRef.current.startCellEditMode({ id: newId, field: "descripcion" });
        } catch (err) {
          console.log("Error: ", err);
        }
      });

      return updated;
    });
  };

  // Eliminar línea (boton papelera de la columna acciones, como la proforma).
  const handleDeleteRow = (id) => setItems((prev) => prev.filter((r) => r.id !== id));

  // Función para actualizar fila (mismo patron processRowUpdate de la proforma).
  const processRowUpdate = (newRow, oldRow) => {
    // Normalizar numeros: convertir "31,00" -> 31.00 (float).
    const cantidad = parseFloat(String(newRow.cantidad ?? "").replace(",", ".")) || 0;
    const precioUnitario = parseFloat(String(newRow.precio_unitario ?? "").replace(",", ".")) || 0;

    // Crear la fila actualizada (descripcion LIBRE en mayusculas — convencion del sistema).
    const updatedRow = {
      ...newRow,
      descripcion: upper(newRow.descripcion || ""),
      cantidad,
      precio_unitario: precioUnitario,
    };

    // Actualizar estado global de items.
    setItems((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));

    return updatedRow; // obligatorio retornar la fila actualizada
  };

  // Campos del DataGrid (mismo estilo de columnas que la proforma).
  const columns = [
    { field: "id", headerName: "ID", editable: false },
    {
      field: "descripcion",
      headerName: "Descripción",
      flex: 4,
      editable: true,
      headerAlign: "center",
    },
    {
      field: "cantidad",
      headerName: "Cantidad",
      flex: 1,
      type: "number",
      editable: true,
      headerAlign: "center",
      renderCell: (params) => {
        // safe check: params puede ser undefined muy tempranamente, evitamos crash
        const raw = params?.value ?? 0;
        return formatDecimal(raw);
      },
    },
    {
      field: "precio_unitario",
      // Etiqueta dinamica: codigo de moneda de la OC (como hace la proforma).
      headerName: `Precio Unitario (${monedaCodigo(form.moneda_id) || "Bs."})`,
      flex: 1,
      type: "number",
      editable: true,
      headerAlign: "center",
      renderCell: (params) => {
        // safe check: params puede ser undefined muy tempranamente, evitamos crash
        const raw = params?.value ?? 0;
        return formatDecimal(raw);
      },
    },
    {
      field: "aplica_iva",
      headerName: "IVA",
      flex: 0.6,
      type: "boolean",
      editable: true,
      headerAlign: "center",
    },
    {
      field: "iva_categoria_id",
      headerName: "Alícuota",
      flex: 1,
      editable: true,
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: (ivas || []).map((x) => ({ value: x.id, label: `${formatDecimal(x.tasa_porcentaje)}%` })),
      renderCell: (params) => {
        if (!params || !params.row || !params.row.aplica_iva) return "";
        const cat = (ivas || []).find((x) => x.id === Number(params.value));
        return cat ? `${formatDecimal(cat.tasa_porcentaje)}%` : "";
      },
    },
    {
      field: "total",
      // Etiqueta dinamica.
      headerName: `Total (${monedaCodigo(form.moneda_id) || "Bs."})`,
      flex: 1,
      sortable: false,
      align: "right",        // alinea el contenido a la derecha
      headerAlign: "center", // alinea el título de la cabecera
      renderCell: (params) => {
        if (!params || !params.row) return "0,00";

        const cantidad = Number(params.row.cantidad) || 0;
        const precio = Number(params.row.precio_unitario) || 0;
        const alic = params.row.aplica_iva
          ? (Number((ivas.find((x) => x.id === Number(params.row.iva_categoria_id)) || {}).tasa_porcentaje) || 0)
          : 0;
        const total = cantidad * precio * (1 + alic / 100);
        return formatDecimal(total);
      },
    },
    {
      field: "acciones",
      headerName: "",
      flex: 0.4,
      sortable: false,
      renderCell: (params) => (
        <Button
          color="bg-slate-800"
          size="small"
          onClick={() => handleDeleteRow(params.row.id)}>
          <i className="fa-solid fa-lg fa-trash me-3"></i>
        </Button>
      ),
    },
  ];

  // VALIDACION — mismo patron que FormProformas (Ventas): construye newErrors +
  // errorToast, hace setErrors(newErrors), muestra un unico toast.error con el
  // listado de errores y devuelve un booleano. Espeja las reglas del backend
  // (proveedor + fecha obligatorios + al menos un item valido con precio).
  const validate = () => {
    const newErrors = {};
    var errorToast = [];

    if (!form.proveedor_id) {
      newErrors.proveedor_id = "Proveedor es obligatorio";
      errorToast.push("- Debe seleccionar un proveedor.");
    }
    if (!form.fecha_emision) {
      newErrors.fecha_emision = "Fecha de emisión es obligatoria";
      errorToast.push("- Debe indicar la fecha de emisión.");
    }
    // (2026-06-10) Condición de pago OBLIGATORIA (observaciones sigue siendo
    // opcional por ser campo de notas). Vacío o solo espacios no vale.
    if (!String(form.condicion_pago || "").trim()) {
      newErrors.condicion_pago = "Condición de pago es obligatoria";
      errorToast.push("- Condición de pago es obligatoria (ej.: CONTADO, CRÉDITO 30 DÍAS)");
    }

    // Al menos un item valido: descripcion + cantidad > 0 + precio > 0.
    const validos = items.filter(
      (it) => it.descripcion && Number(it.cantidad) > 0 && Number(it.precio_unitario) > 0
    );
    if (!validos.length) {
      if (newErrors.items === undefined) newErrors.items = [];
      newErrors.items.push("Debe agregar al menos un item valido (descripcion, cantidad y precio).");
      errorToast.push("- Debe agregar al menos un ítem válido (descripción, cantidad y precio).");
    }

    // Items con datos parciales (tienen descripcion pero cantidad/precio invalidos).
    items.forEach((it, i) => {
      const tieneDatos = !!it.descripcion || it.cantidad !== "" || it.precio_unitario !== "";
      if (!tieneDatos) return;
      if (it.descripcion && Number(it.cantidad) <= 0) {
        if (newErrors.items === undefined) newErrors.items = [];
        newErrors.items.push(`Línea ${i + 1}: la cantidad debe ser mayor que 0.`);
        errorToast.push(`- Línea ${i + 1}: la cantidad debe ser mayor que 0.`);
      }
      if (it.descripcion && Number(it.precio_unitario) <= 0) {
        if (newErrors.items === undefined) newErrors.items = [];
        newErrors.items.push(`Línea ${i + 1}: el precio unitario debe ser mayor que 0.`);
        errorToast.push(`- Línea ${i + 1}: el precio unitario debe ser mayor que 0.`);
      }
    });

    setErrors(newErrors);
    if (errorToast.length > 0) {
      toast.error(<div>
        {errorToast.map(item => (
            <span className="text-start">{item}<br/></span>
          ))}
      </div>)
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    // Payload EXACTO de siempre: el `id` interno de la grilla NO viaja.
    const validos = items
      .filter((it) => it.descripcion && Number(it.cantidad) > 0 && Number(it.precio_unitario) > 0)
      .map((it) => ({
        descripcion: it.descripcion,
        cantidad: it.cantidad,
        precio_unitario: it.precio_unitario,
        aplica_iva: it.aplica_iva,
        iva_categoria_id: it.aplica_iva ? (it.iva_categoria_id || null) : null,
      }));

    setButtonDisabled(true);
    try {
      if (isEdit) {
        await updateOrdenCompra(decryptText(ocIdHash), { ...form, items: validos });
        toast.success("OC actualizada.");
      } else {
        await createOrdenCompra({ ...form, items: validos });
        toast.success("OC creada.");
      }
      redirectToList();
    } catch (err) {
      handleApiError(err);
    } finally {
      setButtonDisabled(false);
    }
  };

  return (
    <AdminLayout nombreUsuario={user?.nombre || "Usuario"}>
      {/* Contenedor principal con separación (mismo wrapper que la pagina de Proformas) */}
      <div className="mt-8 px-4 md:px-8">
        <div className="mx-auto w-full">
          <div className="flex flex-wrap">
            <div className="w-full lg:w-12/12 px-4">
              <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0">
                {/* Colocamos el contenedor de las notificaciones */}
                <ToastContainer />
                <div className="rounded-t bg-white mb-0 px-6 py-6">
                  <div className="text-center flex justify-between">
                    <h6 className="text-blueGray-700 text-xl font-bold">{isEdit ? "Actualizar" : "Crear"} Orden de Compra</h6>
                    {isEdit && (
                      <h6 className="text-blueGray-400 text-sm mt-1 font-bold uppercase">Estatus: {estatus}</h6>
                    )}
                  </div>
                </div>
                <div className="flex-auto px-4 lg:px-10 py-10 pt-0 bg-white">
                  <div className="w-full flex flex-wrap px-4 pt-4">
                    <div className="w-full lg:w-6/12 px-0">
                      <h6 className="text-blueGray-400 text-sm mt-3 my-6 font-bold uppercase">Información de la Orden de Compra</h6>
                    </div>
                  </div>
                  {loading ? (
                    <p className="text-blueGray-400 italic px-4 py-8 text-center">Cargando orden de compra…</p>
                  ) : (
                    <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
                      <hr className="my-6 border-b-1 border-blueGray-300"/>
                      {/* ============ CABECERA (patron de columnas w-X/12 de la proforma) ============ */}
                      <div className="flex flex-wrap">
                        <div className="w-full lg:w-4/12 px-4">
                          <div className="relative w-full mb-3">
                            <label className="block text-blueGray-600 text-xs font-bold mb-2">Proveedor</label>
                            <select
                              className={INPUT_CLASS}
                              value={form.proveedor_id}
                              onChange={set("proveedor_id")}
                            >
                              <option value="">Seleccione...</option>
                              {(provs || []).map((p) => (
                                <option key={p.id} value={p.id}>{p.nombre}</option>
                              ))}
                            </select>
                            {errors.proveedor_id && <p className="text-red-500 text-xs mt-1">{errors.proveedor_id}</p>}
                          </div>
                        </div>
                        <div className="w-full lg:w-2/12 px-4">
                          <div className="relative w-full mb-3">
                            <label className="block text-blueGray-600 text-xs font-bold mb-2">Fecha de emisión</label>
                            <input
                              type="date"
                              className={INPUT_CLASS}
                              value={form.fecha_emision} max={hoyISO()}
                              placeholder="dd/mm/yyyy"
                              onChange={set("fecha_emision")}
                            />
                            {errors.fecha_emision && <p className="text-red-500 text-xs mt-1">{errors.fecha_emision}</p>}
                          </div>
                        </div>
                        <div className="w-full lg:w-2/12 px-4">
                          <div className="relative w-full mb-3">
                            <label className="block text-blueGray-600 text-xs font-bold mb-2">Moneda de la orden</label>
                            <select
                              className={INPUT_CLASS}
                              value={form.moneda_id}
                              onChange={set("moneda_id")}
                            >
                              {MONEDAS.map((m) => (
                                <option key={m.id} value={m.id}>{m.codigo}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="w-full lg:w-4/12 px-4">
                          <div className="relative w-full mb-3">
                            <label className="block text-blueGray-600 text-xs font-bold mb-2">Condición de pago</label>
                            <input
                              type="text"
                              className={INPUT_CLASS}
                              value={form.condicion_pago}
                              placeholder="CONTADO, CRÉDITO 30 DÍAS..."
                              onChange={(e) => setForm((f) => ({ ...f, condicion_pago: upper(e.target.value) }))}
                            />
                            {errors.condicion_pago && <p className="text-red-500 text-xs mt-1">{errors.condicion_pago}</p>}
                          </div>
                        </div>
                        <div className="w-full lg:w-12/12 px-4">
                          <div className="relative w-full mb-3">
                            <label className="block text-blueGray-600 text-xs font-bold mb-2">Observaciones</label>
                            <input
                              type="text"
                              className={INPUT_CLASS}
                              value={form.observaciones}
                              placeholder="OBSERVACIONES..."
                              onChange={(e) => setForm((f) => ({ ...f, observaciones: upper(e.target.value) }))}
                            />
                          </div>
                        </div>
                      </div>
                      {/* ============ EDITOR DE ITEMS (boton + DataGrid, identico a la proforma) ============ */}
                      <div className="flex flex-wrap">
                        <div className="w-full lg:w-12/12 px-4 mt-1 text-right">
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddRow}
                            style={{ marginBottom: 10 }}>
                            Agregar línea
                          </Button>
                          <DataGrid
                            apiRef={apiRef}
                            rows={items}
                            columns={columns}
                            getRowId={(row) => row.id}
                            pageSize={5}
                            autoHeight
                            processRowUpdate={processRowUpdate}
                            isCellEditable={(params) =>
                              params.field !== "iva_categoria_id" || !!params.row.aplica_iva
                            }
                            columnVisibilityModel={{
                              id: false, // oculta la columna "id"
                            }}
                            rowHeight={30}
                            localeText={esES.components.MuiDataGrid.defaultProps.localeText} // Traducción al español
                            sx={{
                              '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 'bold',   // ahora sí en negrita
                                fontSize: '0.80rem',
                              },
                            }}
                          />
                        </div>
                        <div className="w-full lg:w-12/12 px-5 my-1 text-center">
                          {errors.items &&
                            <p className="text-red-500 text-xs mt-1">
                              {errors.items.map(item => (
                                <span className="text-start">{item}<br/></span>
                              ))}
                          </p>}
                        </div>
                        {/* ============ TOTALES (bloque identico a la proforma) ============ */}
                        <div className="w-full lg:w-8/12 px-4"></div>
                        <div className="w-full lg:w-4/12 px-4 mt-3">
                          <div className="flex w-full mb-1">
                            <div className="w-full lg:w-5/12 px-4 mt-3">
                              <label className="block text-blueGray-600 font-bold text-lg">Base imponible</label>
                            </div>
                            <div className="w-full lg:w-7/12 px-4 mt-3 text-right pr-0">
                              <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                              {formatMoney(taxBase, monedaObj)}
                              </label>
                            </div>
                          </div>
                          <div className="flex w-full mb-1">
                            <div className="w-full lg:w-4/12 px-4">
                              <label className="block text-blueGray-600 font-bold text-lg">I.V.A.</label>
                            </div>
                            <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                              <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                              {formatMoney(taxApplied, monedaObj)}
                              </label>
                            </div>
                          </div>
                          <div className="flex w-full mb-3">
                            <div className="w-full lg:w-4/12 px-4">
                              <label className="block text-blueGray-600 font-bold text-lg">Total</label>
                            </div>
                            <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                              <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                              {formatMoney(totalAmount, monedaObj)}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ============ FOOTER (botones via constantes formHelpers) ============ */}
                      <div className={FORM_FOOTER_CONTAINER}>
                        <button
                          type="button"
                          className={FORM_BTN_CANCEL}
                          disabled={buttonDisabled}
                          onClick={() => redirectToList()}
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          className={FORM_BTN_SUBMIT}
                          disabled={buttonDisabled}
                        >
                          {buttonDisabled ? (isEdit ? "Actualizando..." : "Guardando...") : isEdit ? "Actualizar" : "Guardar"}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
